import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RestoreIcon from '@mui/icons-material/Restore';
import { RecentValuesMenu } from '../RecentValuesMenu';
import { pickableRecent, type PersonPickerConfig } from '../personPicker';
import { FIELD_END_BUTTON_SX } from '../fieldEndButton';

/**
 * F2 "the one from a moment ago" for a whole record FORM.
 *
 * Configured from the very list the grid cells use — `useInlinePhotoFields`,
 * `useInlineFindFields`, … — so a field offers the same values in the dialog as
 * in the table, and which fields offer anything at all is decided once. Two
 * lists would drift the day somebody adds a field to one of them.
 *
 * ONE menu for the dialog, not one per field. The renderers under ./ are plain
 * functions called during the form's render pass (see ./binding for why), so
 * they cannot hold hooks — a form that renders a section conditionally would
 * change the hook order and crash. The state therefore lives here, where the
 * dialog calls this once, and the fields get plain callbacks.
 */
export interface FormRecentValues {
    /** Spread into `bindFormFields` — the renderers pick these up per field. */
    binding: {
        recentKeyDown: (
            name: string
        ) => ((event: KeyboardEvent<HTMLElement>) => void) | undefined;
        /**
         * The same as a button — BARE: an Autocomplete adds it to its own
         * indicators, a text field wraps it in an InputAdornment.
         */
        recentTrigger: (name: string) => ReactNode;
    };
    /** Render once inside the dialog; it anchors itself to the field. */
    menu: ReactNode;
}

/** What this needs off a field spec — the grid's `InlineField`, structurally. */
interface RecentField {
    field: string;
    recent?: readonly string[];
    /** Which editor the field uses; only `person` is treated specially here. */
    editor?: { kind: string };
}

export function useFormRecentValues({
    fields,
    setValue,
    personPicker
}: {
    /** The record's inline-edit field list, recent values already attached. */
    fields: readonly RecentField[];
    /** Writes the picked value into the form, dirtying it as a keystroke would. */
    setValue: (name: string, value: string) => void;
    /**
     * The form's own person picker — its accounts already narrowed to the
     * record's day — so a name from another record is offered only if this
     * one could have picked it.
     */
    personPicker: PersonPickerConfig;
}): FormRecentValues {
    const intl = useIntl();
    const picker = personPicker;
    const [open, setOpen] = useState<{
        name: string;
        anchor: HTMLElement;
    } | null>(null);

    const valuesFor = (name: string): readonly string[] => {
        const spec = fields.find((entry) => entry.field === name);
        if (!spec?.recent) {
            return [];
        }
        // A PERSON field offers only what it could also have been picked from:
        // these values come off OTHER records, and the menu writes the picked
        // one straight in — the very route the picker closes (see
        // pickableRecent). The field's own value needs no offering.
        return spec.editor?.kind === 'person'
            ? pickableRecent(picker, spec.recent, '')
            : spec.recent;
    };

    const label = intl.formatMessage({ id: 'recent.tooltip' });

    function offers(name: string): boolean {
        return valuesFor(name).length > 0;
    }

    return {
        binding: {
            recentKeyDown: (name) =>
                offers(name)
                    ? (event) => {
                          if (event.key !== 'F2') {
                              return;
                          }
                          event.preventDefault();
                          setOpen({ name, anchor: event.currentTarget });
                      }
                    : undefined,
            recentTrigger: (name) =>
                offers(name) ? (
                    <Tooltip title={label}>
                        <IconButton
                            size="small"
                            aria-label={label}
                            sx={FIELD_END_BUTTON_SX}
                            // The field keeps the caret: opening the list must
                            // not read as leaving the input.
                            onMouseDown={(event) => event.preventDefault()}
                            // Out of the Tab order, like MUI's clear X: the
                            // field has F2 for it, and Tab leaving a field
                            // goes to the next field (see tabTakesSuggestion).
                            tabIndex={-1}
                            onClick={(event) =>
                                setOpen({ name, anchor: event.currentTarget })
                            }
                        >
                            <RestoreIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : null
        },
        menu: (
            <RecentValuesMenu
                anchor={open?.anchor ?? null}
                values={open ? valuesFor(open.name) : []}
                /**
                 * The list marks what the field holds; reading it back out of
                 * the form here would subscribe the whole dialog to every
                 * keystroke of every field, which is what `watch` costs (see
                 * renderTextField). A form field is wide enough to read
                 * anyway — the marker is a nicety the grid cell needs more.
                 */
                current=""
                onPick={(picked) => {
                    if (open) {
                        setValue(open.name, picked);
                    }
                    setOpen(null);
                }}
                onClose={() => setOpen(null)}
            />
        )
    };
}
